/**
 * Design System Component Exports
 * 
 * This file re-exports all UI components with design system adaptations.
 * Import components from here instead of directly from shadcn to ensure
 * design system consistency.
 * 
 * NOTE: You can now use path aliases to import design tokens directly:
 * - @ds/tokens - Design tokens (colors, spacing, etc.)
 * - @ds/themes - Theme definitions
 * - @ds/types - Shared interfaces
 * - @ds/utils - Utilities
 */

// Import shared design tokens
import { colors, spacing, typography } from '@repo/design-system/tokens';

// Re-export adapted components
export { Button } from '../adapters/Button';
export { 
  containerVariants,
  itemVariants,
  buttonVariants,
  statsVariants,
  statItemVariants,
  fadeInVariants,
  scaleUpVariants,
  listItemVariants,
  // Industry animation variants
  industryCardVariants,
  industryGridVariants,
  industryIconVariants,
  featureItemVariants,
  ctaButtonVariants,
  categoryButtonVariants,
  sectionTitleVariants,
  sectionDescriptionVariants
} from '../adapters/Animations';
export { 
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent
} from '../adapters/Card';
export { IconContainer } from '../adapters/IconContainer';
export { Input } from '../adapters/Input';
export { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../adapters/Select';
export { FormField } from '../adapters/FormField';
export { 
  Text,
  Heading
} from '../adapters/Text';
export { ThemeSwitcher } from '../adapters/ThemeSwitcher';
export { ThemeProvider, useTheme } from '../adapters/ThemeProvider';
export { Footer } from '../../footer/Footer';
export { default as LiveChat } from '../adapters/LiveChat';
export { SkeletonLoader } from '../adapters/SkeletonLoader';

// Export Legal Components
export {
  LegalContent,
  LegalSection,
  LegalSubsection,
  LegalParagraph,
  LegalList,
  LegalDefinition,
  LegalHighlight
} from '../adapters/LegalContent';

// Industry animation variants are now part of the main Animations adapter

// Re-export components that don't have adapters yet
// These will be adapted in the future
export { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from '../avatar';
export { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../tabs';
export { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '../alert-dialog';
export { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../dropdown-menu';
export { Sonner, toast } from '../adapters/Sonner';
export { Checkbox } from '../checkbox';
export { Switch } from '../switch';
export { 
  Alert, 
  AlertTitle, 
  AlertDescription 
} from '../alert';
export { Label } from '../label';
export { Badge, badgeVariants } from '../badge';


// Re-export shared design tokens for direct use
export { colors, spacing, typography };

// Export the real ProductViewer with fallback support
export { ProductViewer } from '../adapters/ProductViewer';

// Export the new LoadingSpinner component
export { default as LoadingSpinner } from '../adapters/LoadingSpinner';
/**
 * Usage:
 * 
 * ```tsx
 * // Instead of this:
 * import { Button } from '@/components/ui/button'
 * 
 * // Do this:
 * import { Button } from '@/components/ui/design-system'
 * ```
 */ 